import { Controller } from "@hotwired/stimulus"

import { edgesOf, pageStep } from "../carousel/position"

// The previous and next buttons of a `carousel` album (`animal_gallery`
// block, phase 074). The row itself is CSS: one line of photos that scrolls
// sideways with snap points, by swipe on a phone and by the arrow keys once
// the focusable row has focus — and without JavaScript it is exactly that,
// with the buttons left `hidden`. This controller only shows the buttons, when
// the row overflows, scrolls by a view on a press and disables the button of
// an edge the row has reached. The photos stay links PhotoSwipe opens
// (`gallery` on the row, a controller of its own element).
export default class CarouselController extends Controller<HTMLElement> {
  static targets = ["track", "controls", "previous", "next"]

  declare readonly trackTarget: HTMLElement
  declare readonly controlsTarget: HTMLElement
  declare readonly previousTarget: HTMLButtonElement
  declare readonly nextTarget: HTMLButtonElement

  private resizeObserver: ResizeObserver | null = null

  connect(): void {
    this.trackTarget.addEventListener("scroll", this.update, { passive: true })
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.update)
      this.resizeObserver.observe(this.trackTarget)
    }
    this.update()
  }

  disconnect(): void {
    this.trackTarget.removeEventListener("scroll", this.update)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.controlsTarget.hidden = true
  }

  previous(): void {
    this.scrollByView(-1)
  }

  next(): void {
    this.scrollByView(1)
  }

  private scrollByView(direction: -1 | 1): void {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.trackTarget.scrollBy({ left: direction * pageStep(this.trackTarget), behavior: reduced ? "auto" : "smooth" })
  }

  private readonly update = (): void => {
    const edges = edgesOf(this.trackTarget)
    this.controlsTarget.hidden = !edges.scrollable
    this.previousTarget.disabled = edges.atStart
    this.nextTarget.disabled = edges.atEnd
  }
}
